import { useCallback, useRef, useState, type ReactNode } from 'react';
import * as Auth from '@/contexts/AuthContext';
import { useLocale } from '@/i18n/useLocale';
import { ReportTranslationDialog } from './ReportTranslationDialog';

/**
 * The wiring for one "Report a translation" trigger (#2120). The
 * MachineTranslationBanner and the LanguageSwitcher each call this and render
 * their own button; both open ReportTranslationDialog (see that file for what
 * a report carries and why).
 *
 * Offered only to a signed-in person reading a non-English page: the endpoint
 * is authenticated by design, and in English there is no translation to
 * report. Staff whose locale is pinned to English never see the switcher, and
 * the banner never shows in English.
 */

/** Mirrors UI_REPORT_LIMITS in backend/src/routes/translation-reports.js. */
export const REPORT_LIMITS = { selected_text: 500, note: 1000 } as const;

/** The text currently highlighted on the page, whitespace-collapsed and capped. */
export function readSelectedText(): string {
  try {
    const raw = window.getSelection?.()?.toString() ?? '';
    return raw.replace(/\s+/g, ' ').trim().slice(0, REPORT_LIMITS.selected_text);
  } catch {
    return '';
  }
}

/**
 * `useOptionalAuth`, resolved once at module load. The triggers only DECIDE
 * whether to show themselves, so outside an AuthProvider they stay hidden
 * rather than throwing. Many shell tests replace AuthContext with a double
 * that exports only `useAuth`; reading a missing export off such a double
 * throws, so the lookup happens here, once, and falls back to "no session".
 * The hook is then called unconditionally on every render.
 */
const useOptionalSession: () => { user?: unknown } | null = (() => {
  try {
    return typeof Auth.useOptionalAuth === 'function' ? Auth.useOptionalAuth : () => null;
  } catch {
    return () => null;
  }
})();

export function useReportTranslation(): {
  available: boolean;
  triggerProps: { onPointerDown: () => void; onClick: () => void };
  dialog: ReactNode;
} {
  const session = useOptionalSession();
  const { locale } = useLocale();
  const [open, setOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [opening, setOpening] = useState(0);
  const atPointerDown = useRef('');

  // The selection is read on pointer-down AND on click: pressing a button can
  // collapse the page selection in some browsers before `click` fires, so the
  // pointer-down read is kept and the click read wins only when it still has
  // text (a keyboard press has no pointer-down).
  const onPointerDown = useCallback(() => {
    atPointerDown.current = readSelectedText();
  }, []);
  const onClick = useCallback(() => {
    setSelectedText(readSelectedText() || atPointerDown.current);
    atPointerDown.current = '';
    // A fresh key per opening resets the note, the phase and any error.
    setOpening((n) => n + 1);
    setOpen(true);
  }, []);

  const available = Boolean(session?.user) && locale !== 'en';

  return {
    available,
    triggerProps: { onPointerDown, onClick },
    dialog: available ? (
      <ReportTranslationDialog
        key={opening}
        open={open}
        onOpenChange={setOpen}
        selectedText={selectedText}
        locale={locale}
        noteMax={REPORT_LIMITS.note}
      />
    ) : null,
  };
}
